/**
 * Notifications settings panel (A6) — manage who gets emailed when an
 * alarm fires. Lives on the /app/alarms route's "Notifications" tab.
 *
 * Recipients (add/remove) + a minimum-severity gate + a master enabled
 * toggle, wired to GET/PUT `/v1/iot/alarms/notify-settings` in live mode.
 * In stub mode (no live API) the same UI drives local component state so
 * the panel is fully usable for design review without a backend.
 *
 * Saving recipients here only ARMS email delivery — the platform still
 * sends nothing until the operator configures AWS SES (verified sender +
 * IAM ses:SendEmail). The hint banner spells that out.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Info, Mail, Plus, Save, X } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import { EmptyState } from "@/lib/ui/empty-state";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { alarmNotify, isLiveApi, type NotifySettings } from "@/lib/api";

type Severity = NotifySettings["min_severity"];

const SEVERITIES: Array<{ value: Severity; label: string }> = [
  { value: "info", label: "Info & above" },
  { value: "warning", label: "Warning & above" },
  { value: "critical", label: "Critical only" },
];

const STUB_DEFAULT: NotifySettings = {
  org_id: "stub",
  recipients: [],
  min_severity: "warning",
  enabled: true,
};

/** Minimal client-side email shape check — mirrors the server's permissive gate. */
function isValidEmail(s: string): boolean {
  const at = s.indexOf("@");
  if (at <= 0 || at !== s.lastIndexOf("@")) return false;
  const domain = s.slice(at + 1);
  return domain.length >= 3 && domain.includes(".") && !/\s/.test(s);
}

export function NotificationsSettings() {
  // BEHAVIOR CHANGE: editing notification settings is now org.admin-only,
  // mirroring the backend (notify-settings PUT is RequireRole(admin); members
  // lost edit). GET stays viewer-readable, so the displayed values remain
  // visible — only the save/add/remove affordances are admin-gated. `canWrite`
  // here is the local "can edit these settings" flag, now backed by org.admin.
  const { can } = useOrg();
  const canWrite = can("org.admin");
  const toast = useToast();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["alarm-notify-settings"],
    queryFn: alarmNotify.get,
    enabled: isLiveApi,
    staleTime: 30_000,
  });

  // Local editable draft. Seeded from the server (live) or the stub default,
  // then owned by the form until saved.
  const [recipients, setRecipients] = useState<string[]>([]);
  const [minSeverity, setMinSeverity] = useState<Severity>("warning");
  const [enabled, setEnabled] = useState(true);
  const [draftEmail, setDraftEmail] = useState("");
  const [seeded, setSeeded] = useState(!isLiveApi);

  // Hydrate the draft once the live settings arrive (or immediately in stub).
  useEffect(() => {
    if (!isLiveApi) {
      setRecipients(STUB_DEFAULT.recipients);
      setMinSeverity(STUB_DEFAULT.min_severity);
      setEnabled(STUB_DEFAULT.enabled);
      return;
    }
    if (settingsQuery.data && !seeded) {
      setRecipients(settingsQuery.data.recipients ?? []);
      setMinSeverity(settingsQuery.data.min_severity ?? "warning");
      setEnabled(settingsQuery.data.enabled ?? true);
      setSeeded(true);
    }
  }, [settingsQuery.data, seeded]);

  const saveMutation = useMutation({
    mutationFn: () =>
      alarmNotify.put({ recipients, min_severity: minSeverity, enabled }),
    onSuccess: (saved) => {
      queryClient.setQueryData(["alarm-notify-settings"], saved);
      toast.push("ok", "Notification settings saved");
    },
    onError: () =>
      toast.push("error", "Could not save notification settings"),
  });

  const draftValid = draftEmail.trim() !== "" && isValidEmail(draftEmail.trim());
  const duplicate = useMemo(
    () => recipients.some((r) => r.toLowerCase() === draftEmail.trim().toLowerCase()),
    [recipients, draftEmail],
  );

  const addRecipient = () => {
    const e = draftEmail.trim();
    if (!draftValid || duplicate) return;
    setRecipients((prev) => [...prev, e]);
    setDraftEmail("");
  };

  const removeRecipient = (email: string) =>
    setRecipients((prev) => prev.filter((r) => r !== email));

  const save = () => {
    if (!canWrite) return;
    if (isLiveApi) {
      saveMutation.mutate();
    } else {
      toast.push("ok", "Saved (stub mode — not persisted)");
    }
  };

  const saving = saveMutation.isPending;
  const disabled = !canWrite || saving;

  return (
    <div className="flex max-w-2xl flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-base font-semibold text-on-surface">
          <BellRing className="size-4 text-on-surface-muted" />
          Notifications
        </h2>
        <p className="text-xs text-on-surface-muted">
          Email operators when an alarm fires. Only firings at or above the
          chosen severity are sent.
        </p>
      </div>

      {/* Operator-config hint — saving recipients alone doesn't send mail. */}
      <div className="flex gap-2.5 rounded-lg border border-info/40 bg-info/10 p-3 text-xs text-on-surface">
        <Info className="mt-0.5 size-4 shrink-0 text-info" />
        <p>
          If these recipients get no mail, email delivery has not been switched
          on yet: ask your Sahas contact. Recipients are saved either way.
          SMS delivery is planned for a future release.
        </p>
      </div>

      {/* Master toggle */}
      <label
        className={cn(
          "flex cursor-pointer items-center justify-between rounded-lg border border-border bg-surface-elevated p-3",
          !canWrite && "cursor-not-allowed opacity-70",
        )}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-on-surface">
            Email notifications
          </span>
          <span className="text-xs text-on-surface-muted">
            {enabled ? "On — firing alarms are emailed" : "Off — no alarm emails"}
          </span>
        </div>
        <input
          type="checkbox"
          className="size-4 accent-brand"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
      </label>

      {/* Minimum severity */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Minimum severity
        </span>
        <div className="flex gap-1.5">
          {SEVERITIES.map((s) => {
            const active = minSeverity === s.value;
            return (
              <button
                key={s.value}
                type="button"
                disabled={disabled}
                onClick={() => setMinSeverity(s.value)}
                className={cn(
                  "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                  active
                    ? "border-brand bg-brand text-brand-foreground"
                    : "border-border bg-surface text-on-surface hover:border-brand/60",
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Recipients */}
      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Recipient emails
        </span>

        {canWrite && (
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={draftEmail}
              disabled={disabled}
              placeholder="operator@plant.com"
              onChange={(e) => setDraftEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addRecipient();
                }
              }}
              aria-label="Add recipient email"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || !draftValid || duplicate}
              onClick={addRecipient}
            >
              <Plus className="mr-1 size-3.5" />
              Add
            </Button>
          </div>
        )}
        {draftEmail.trim() !== "" && !draftValid && (
          <p className="text-xs text-danger">Enter a valid email address.</p>
        )}
        {duplicate && (
          <p className="text-xs text-warning">That address is already added.</p>
        )}

        {recipients.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No recipients yet"
            description="Add an email to start receiving alarm notifications."
          />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {recipients.map((email) => (
              <li
                key={email}
                className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
              >
                <span className="flex items-center gap-2 text-sm text-on-surface">
                  <Mail className="size-3.5 text-on-surface-muted" />
                  <span className="font-mono text-xs">{email}</span>
                </span>
                {canWrite && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => removeRecipient(email)}
                    className="rounded p-1 text-on-surface-muted transition-colors hover:bg-surface-muted hover:text-danger disabled:opacity-50"
                    title="Remove recipient"
                    aria-label={`Remove ${email}`}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {canWrite ? (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <span className="text-xs text-on-surface-muted">
            {recipients.length} recipient{recipients.length === 1 ? "" : "s"}
            {!enabled && " · notifications off"}
          </span>
          <Button size="sm" onClick={save} disabled={disabled}>
            <Save className="mr-1.5 size-3.5" />
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      ) : (
        <Badge variant="secondary" className="self-start">
          Read-only — ask an org admin to edit notifications
        </Badge>
      )}
    </div>
  );
}
